import { useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, KeyIcon, MapPinIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchAssignments } from '../store/assignmentsSlice';
import { fetchInventory } from '../store/keyInventorySlice';
import { fetchPeople } from '../store/peopleSlice';
import { fetchKeyTypes } from '../store/keyTypesSlice';
import Card from '../components/Card';
import Button from '../components/Button';

export default function KeyTypeDetail() {
  const { keyTypeId } = useParams<{ keyTypeId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const keyTypes = useAppSelector((state) => state.keyTypes.keyTypes);
  const inventory = useAppSelector((state) => state.keyInventory.inventory);
  const assignments = useAppSelector((state) => state.assignments.assignments);
  const people = useAppSelector((state) => state.people.people);
  const locations = useAppSelector((state) => state.locations.locations);
  const loading = useAppSelector((state) => 
    state.keyTypes.loading || state.keyInventory.loading || state.assignments.loading || state.people.loading
  );

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchKeyTypes());
    dispatch(fetchInventory());
    dispatch(fetchAssignments());
    dispatch(fetchPeople());
  }, [dispatch]);

  // Find the key type
  const keyType = useMemo(() => {
    return keyTypes.find(kt => kt.id === keyTypeId);
  }, [keyTypes, keyTypeId]);

  // Get all inventory items for this key type
  const keyTypeInventory = useMemo(() => {
    return inventory.filter(item => item.keyTypeId === keyTypeId);
  }, [inventory, keyTypeId]);

  // Calculate counts by location
  const countsByLocation = useMemo(() => {
    const counts: { locationId: string; locationName: string; locationColor: string; total: number; assigned: number; available: number }[] = [];
    
    keyTypeInventory.forEach(item => {
      const location = locations.find(loc => loc.id === item.locationId);
      const assignedCount = assignments
        .filter(a => a.keyInventoryItemId === item.id && a.status === 'active')
        .reduce((sum, a) => sum + a.quantity, 0);
      
      counts.push({
        locationId: item.locationId,
        locationName: location?.name || 'Unknown',
        locationColor: location?.color || '#6B7280',
        total: item.quantity,
        assigned: assignedCount,
        available: item.quantity - assignedCount,
      });
    });

    return counts.sort((a, b) => a.locationName.localeCompare(b.locationName));
  }, [keyTypeInventory, locations, assignments]);

  // Get all people who have this key type assigned (active assignments)
  const peopleWithKey = useMemo(() => {
    // Get all inventory item IDs for this key type
    const inventoryIds = new Set(keyTypeInventory.map(item => item.id));
    
    // Find all active assignments for these inventory items
    const activeAssignments = assignments.filter(
      a => inventoryIds.has(a.keyInventoryItemId) && a.status === 'active'
    );

    // Group by person and collect their assignment details
    const personMap = new Map<string, { 
      person: typeof people[0]; 
      assignments: { quantity: number; locationName: string; locationColor: string; inventoryItemId: string }[] 
    }>();

    activeAssignments.forEach(assignment => {
      const person = people.find(p => p.id === assignment.personId);
      if (!person) return;

      const invItem = inventory.find(i => i.id === assignment.keyInventoryItemId);
      const location = invItem ? locations.find(loc => loc.id === invItem.locationId) : null;

      if (!personMap.has(person.id)) {
        personMap.set(person.id, { person, assignments: [] });
      }

      personMap.get(person.id)!.assignments.push({
        quantity: assignment.quantity,
        locationName: location?.name || 'Unknown',
        locationColor: location?.color || '#6B7280',
        inventoryItemId: assignment.keyInventoryItemId,
      });
    });

    // Convert to array and sort by name
    return Array.from(personMap.values()).sort((a, b) => 
      a.person.name.localeCompare(b.person.name)
    );
  }, [keyTypeInventory, assignments, people, inventory, locations]);

  // Calculate totals
  const totals = useMemo(() => {
    return countsByLocation.reduce(
      (acc, loc) => ({
        total: acc.total + loc.total,
        assigned: acc.assigned + loc.assigned,
        available: acc.available + loc.available,
      }),
      { total: 0, assigned: 0, available: 0 }
    );
  }, [countsByLocation]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-secondary-600 dark:text-secondary-400">Loading...</span>
      </div>
    );
  }

  if (!keyType) {
    return (
      <div className="space-y-6">
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back
        </Button>
        <Card>
          <div className="py-12 text-center">
            <KeyIcon className="w-12 h-12 mx-auto text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-100">
              Key Type Not Found
            </h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              The requested key type could not be found.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100 flex items-center gap-2">
            <KeyIcon className="w-7 h-7 text-primary-600" />
            {keyType.name}
          </h1>
          {keyType.description && (
            <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
              {keyType.description}
            </p>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-700 dark:text-blue-400">{totals.total}</div>
            <div className="text-sm text-blue-600 dark:text-blue-500">Total Keys</div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200 dark:border-amber-800">
          <div className="text-center">
            <div className="text-3xl font-bold text-amber-700 dark:text-amber-400">{totals.assigned}</div>
            <div className="text-sm text-amber-600 dark:text-amber-500">Assigned</div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-700 dark:text-green-400">{totals.available}</div>
            <div className="text-sm text-green-600 dark:text-green-500">Available</div>
          </div>
        </Card>
      </div>

      {/* Counts by Location */}
      <Card>
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4 flex items-center gap-2">
          <MapPinIcon className="w-5 h-5 text-secondary-500" />
          Inventory by Location
        </h2>
        {countsByLocation.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-200 dark:border-secondary-700">
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Assigned
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                    Available
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                {countsByLocation.map((loc) => (
                  <tr key={loc.locationId} className="hover:bg-secondary-50 dark:hover:bg-secondary-700/50">
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: loc.locationColor }}
                        />
                        <span className="font-medium text-secondary-900 dark:text-secondary-100">
                          {loc.locationName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-secondary-600 dark:text-secondary-400">
                      {loc.total}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-secondary-600 dark:text-secondary-400">
                      {loc.assigned}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={loc.available <= 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-green-600 dark:text-green-400'}>
                        {loc.available}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-secondary-500 dark:text-secondary-400 text-center py-4">
            No inventory found for this key type.
          </p>
        )}
      </Card>

      {/* People with this Key */}
      <Card>
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4 flex items-center gap-2">
          <UserGroupIcon className="w-5 h-5 text-secondary-500" />
          People with this Key ({peopleWithKey.length})
        </h2>
        {peopleWithKey.length > 0 ? (
          <div className="divide-y divide-secondary-200 dark:divide-secondary-700">
            {peopleWithKey.map(({ person, assignments: personAssignments }) => (
              <div key={person.id} className="flex items-center py-3 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 px-2 -mx-2 rounded">
                {/* Photo/Icon */}
                {person.photoUrl ? (
                  <img
                    src={person.photoUrl}
                    alt={person.name}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                    <UserGroupIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                )}

                {/* Name & Info */}
                <div className="ml-3 flex-1 min-w-0">
                  <Link 
                    to="/people" 
                    className="text-sm font-medium text-secondary-900 dark:text-secondary-100 hover:text-primary-600 dark:hover:text-primary-400"
                  >
                    {person.name}
                  </Link>
                  <div className="flex flex-wrap items-center gap-2 mt-0.5">
                    {person.email && (
                      <span className="text-xs text-secondary-500 dark:text-secondary-400">
                        {person.email}
                      </span>
                    )}
                    {person.department && (
                      <span className="px-1.5 py-0.5 text-xs bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-400 rounded">
                        {person.department}
                      </span>
                    )}
                  </div>
                </div>

                {/* Location badges */}
                <div className="flex flex-wrap gap-1.5 ml-2">
                  {personAssignments.map((assignment, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap"
                      style={{ 
                        backgroundColor: `${assignment.locationColor}15`,
                        borderColor: `${assignment.locationColor}40`,
                        color: assignment.locationColor
                      }}
                    >
                      <span 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: assignment.locationColor }}
                      />
                      {assignment.locationName}
                      {assignment.quantity > 1 && (
                        <span style={{ opacity: 0.7 }}>x{assignment.quantity}</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-secondary-500 dark:text-secondary-400 text-center py-4">
            No one currently has this key assigned.
          </p>
        )}
      </Card>
    </div>
  );
}
